import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Sparkles, Trophy, Flame, Rocket, Star, Zap, Clock } from "lucide-react";
import { toast } from "sonner";
import { useCart, formatInr } from "@/lib/cart";
import { PRICING, usePricing } from "@/lib/pricing";
import { premiumValueList } from "@/lib/data";

const buildPackages = () => [
  {
    id: "elite",
    name: PRICING.tiers.elite.name,
    eyebrow: "Start Your Journey",
    eyebrowIcon: Rocket,
    price: PRICING.tiers.elite.price,
    original: PRICING.tiers.elite.strike,
    tagline: "Perfect for beginners starting their skill development journey.",
    cta: "Choose Your 2 Courses",
    features: [
      "Choose Any 2 Courses",
      "WhatsApp Community Access",
      "Student Support",
      "Learning Resources",
      "Course Updates",
    ],
    highlight: false,
  },
  {
    id: "basic",
    name: PRICING.tiers.basic.name,
    eyebrow: "Most Chosen",
    eyebrowIcon: Star,
    price: PRICING.tiers.basic.price,
    original: PRICING.tiers.basic.strike,
    tagline: "Best for students who want multiple high-income skills.",
    cta: "Choose Your 5 Courses",
    features: [
      "Choose Any 3 Courses",
      "+ 2 E-Lite Courses Free (5 Courses Total)",
      "WhatsApp Community Access",
      "Student Support",
      "Personal Mentorship",
      "Guidance Sessions",
      "Practical Assignments",
      "Skill Development Support",
    ],
    badge: "Most Popular",
    highlight: false,
    popular: true,
  },
  {
    id: "premium",
    name: PRICING.tiers.premium.name,
    eyebrow: "Best Value",
    eyebrowIcon: Trophy,
    price: PRICING.tiers.premium.price,
    original: PRICING.tiers.premium.strike,
    tagline: "Everything you need to transform your career.",
    cta: "Unlock Complete Transformation",
    highlightBox: [
      `All ${PRICING.totalCourses} Courses Included`,
      "Personal Mentorship",
      "WhatsApp Community",
      "Priority Support",
      "1% Community Club Access",
    ],
    features: [
      `Access to All ${PRICING.totalCourses} Courses`,
      "Personal Mentorship Access",
      "WhatsApp Community Access",
      "Priority Support",
      "Premium Learning Resources",
      "Live Guidance Sessions",
      "Career Growth Support",
      "Skill Development Roadmaps",
      "Networking Opportunities",
      "1% Community Club Access",
      "Future Course Updates",
    ],
    badge: "Complete Transformation",
    secondaryBadge: "Recommended",
    highlight: true,
  },
];

function PackageCard({ pkg, idx, onSelect, glow }) {
  const isPremium = pkg.highlight;
  const isPopular = pkg.popular;
  const EyebrowIcon = pkg.eyebrowIcon;
  const savings = pkg.original ? pkg.original - pkg.price : 0;
  const discountPct = pkg.original
    ? Math.round((savings / pkg.original) * 100)
    : 0;

  return (
    <motion.div
      id={pkg.id === "premium" ? "premium-package" : undefined}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: idx * 0.08 }}
      animate={glow ? { scale: [1, 1.03, 1] } : {}}
      data-testid={`package-card-${pkg.id}`}
      className={`relative rounded-[2rem] hairline overflow-hidden flex flex-col scroll-mt-28 transition-shadow duration-500 ${
        isPremium
          ? `lg:scale-105 lg:-my-4 z-10 border bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#06B6D4] text-white ${
              glow
                ? "border-[#06B6D4] ring-4 ring-[#06B6D4]/60 shadow-[0_0_80px_15px_rgba(6,182,212,0.65)]"
                : "border-[#06B6D4]/40 shadow-[0_30px_90px_-25px_rgba(6,182,212,0.65)]"
            }`
          : isPopular
            ? "border border-[#2563EB]/40 ring-1 ring-[#2563EB]/20 bg-white dark:bg-white/[0.04]"
            : "border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04]"
      }`}
    >
      {isPremium && <div className="absolute inset-0 gradient-mesh opacity-30 pointer-events-none" />}

      {(isPremium || isPopular) && (
        <div className="absolute top-5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5">
          <span
            data-testid={`pkg-badge-${pkg.id}`}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold whitespace-nowrap ${
              isPremium ? "bg-[#06B6D4] text-[#0F172A]" : "bg-[#2563EB] text-white"
            }`}
          >
            {isPremium && <Trophy className="h-3 w-3" />}
            {isPopular && <Star className="h-3 w-3" />}
            {pkg.badge}
          </span>
          {isPremium && pkg.secondaryBadge && (
            <span className="inline-flex items-center gap-1 rounded-full bg-white text-[#0F172A] px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold whitespace-nowrap">
              <Flame className="h-3 w-3 text-rose-500" /> {pkg.secondaryBadge}
            </span>
          )}
        </div>
      )}

      <div
        className={`relative p-8 sm:p-10 flex-1 flex flex-col ${
          isPremium ? "pt-20" : isPopular ? "pt-14" : ""
        }`}
      >
        <div
          className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold ${
            isPremium ? "text-[#06B6D4]" : "text-[#2563EB] dark:text-[#06B6D4]"
          }`}
        >
          {EyebrowIcon && <EyebrowIcon className="h-3 w-3" />}
          {pkg.eyebrow}
        </div>

        <h3
          className={`mt-3 font-display text-2xl sm:text-3xl tracking-tight font-semibold ${
            isPremium ? "text-white" : "text-[#0F172A] dark:text-white"
          }`}
        >
          {pkg.name}
        </h3>

        {pkg.tagline && (
          <p
            data-testid={`pkg-tagline-${pkg.id}`}
            className={`mt-2 text-sm leading-relaxed ${
              isPremium ? "text-slate-200/80" : "text-slate-600 dark:text-slate-400"
            }`}
          >
            {pkg.tagline}
          </p>
        )}

        {/* Savings badges */}
        {pkg.original && (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <motion.span
              data-testid={`pkg-save-${pkg.id}`}
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30 px-2.5 py-1 text-[11px] font-bold tracking-wide"
            >
              <Zap className="h-3 w-3 fill-current" /> SAVE {formatInr(savings)}
            </motion.span>
            <span
              data-testid={`pkg-discount-${pkg.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 text-amber-700 dark:text-amber-300 border border-amber-400/40 px-2.5 py-1 text-[11px] font-bold tracking-wide"
            >
              <Flame className="h-3 w-3 fill-current" /> {discountPct}% OFF
            </span>
          </div>
        )}

        {/* Price block */}
        <div className="mt-5">
          {pkg.original && (
            <div
              data-testid={`pkg-original-${pkg.id}`}
              className={`text-base font-medium line-through ${
                isPremium ? "text-slate-300" : "text-slate-400 dark:text-slate-500"
              }`}
            >
              {formatInr(pkg.original)}
            </div>
          )}
          <div className="flex items-end gap-2">
            <span
              data-testid={`pkg-price-${pkg.id}`}
              className={`font-display text-5xl tracking-tighter font-semibold ${
                isPremium ? "text-white" : "text-[#0F172A] dark:text-white"
              }`}
            >
              {formatInr(pkg.price)}
            </span>
            <span
              className={`pb-2 text-xs ${
                isPremium ? "text-slate-300" : "text-slate-500 dark:text-slate-400"
              }`}
            >
              one-time
            </span>
          </div>
        </div>

        <button
          type="button"
          data-testid={`package-cta-${pkg.id}`}
          onClick={() => onSelect(pkg.id)}
          className={`mt-7 inline-flex w-full justify-center items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold hover:-translate-y-0.5 transition-transform ${
            isPremium ? "bg-white text-[#0F172A] hover:shadow-[0_10px_30px_-10px_rgba(255,255,255,0.5)]" : "bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A]"
          }`}
        >
          {pkg.cta} <ArrowRight className="h-4 w-4" />
        </button>

        {isPremium && pkg.highlightBox && (
          <div
            data-testid="pkg-premium-highlight-box"
            className="mt-7 rounded-2xl border border-white/15 bg-white/[0.08] backdrop-blur p-5"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">
              <Sparkles className="h-3 w-3" /> What you get
            </div>
            <ul className="mt-3 space-y-2">
              {pkg.highlightBox.map((h) => (
                <li key={h} className="flex items-center gap-2 text-sm font-medium text-white">
                  <span className="inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-emerald-400 text-[#0F172A]">
                    <Check className="h-2.5 w-2.5" strokeWidth={3} />
                  </span>
                  {h}
                </li>
              ))}
            </ul>
          </div>
        )}

        <ul className={`${isPremium && pkg.highlightBox ? "mt-6" : "mt-8"} space-y-3 flex-1`}>
          {pkg.features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-[14px] leading-relaxed">
              <span
                className={`mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full ${
                  isPremium ? "bg-[#06B6D4] text-[#0F172A]" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                }`}
              >
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              </span>
              <span className={isPremium ? "text-white" : "text-slate-700 dark:text-slate-200"}>{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

function ValueComparison() {
  const { addAllCourses } = useCart();
  const navigate = useNavigate();

  const handleUnlock = () => {
    addAllCourses();
    toast.success(`All ${PRICING.totalCourses} courses added — Premium auto-applied!`);
    navigate("/cart");
  };

  const items = premiumValueList();
  const actualValue = PRICING.premiumActualValue;
  const premium = PRICING.tiers.premium.price;
  const savings = actualValue - premium;
  const discountPct = Math.round((savings / actualValue) * 100);

  const includes = [
    `All ${PRICING.totalCourses} Courses`,
    "Personal Mentorship",
    "Priority Support",
    "1% Community Club",
    "Live Growth Sessions",
    "WhatsApp Community",
    "Premium Resources",
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55 }}
      data-testid="value-comparison"
      className="mt-16 sm:mt-20 relative rounded-[2rem] overflow-hidden border border-[#06B6D4]/30 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#0F172A] text-white"
    >
      <div className="absolute inset-0 gradient-mesh opacity-25 pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 h-80 w-80 rounded-full bg-[#06B6D4]/20 blur-3xl pointer-events-none" />

      <div className="relative p-8 sm:p-12 lg:p-14">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#06B6D4]/40 bg-[#06B6D4]/15 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-[#06B6D4] font-semibold">
            <Sparkles className="h-3 w-3" /> Real Value Comparison
          </span>
          <h3 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold text-balance leading-[1.1]">
            Why Premium Package?
          </h3>
          <p className="mt-3 text-sm sm:text-base text-slate-300 leading-relaxed">
            Look what you&apos;d pay if you bought each piece separately.
          </p>
        </div>

        <div className="mt-10 grid lg:grid-cols-2 gap-6 lg:gap-10">
          {/* Individual breakdown */}
          <div
            data-testid="value-individual-card"
            className="rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur p-6 sm:p-7"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-300 font-semibold">
              Buying Everything Separately
            </div>
            <ul className="mt-5 space-y-2.5 text-sm max-h-[420px] overflow-auto pr-1">
              {items.map((it) => (
                <li
                  key={it.label}
                  data-testid={`value-row-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
                  className="flex items-center justify-between gap-3 text-slate-200"
                >
                  <span className="min-w-0 flex-1">{it.label}</span>
                  <span className="font-medium tabular-nums whitespace-nowrap">
                    {formatInr(it.value)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.22em] text-slate-300 font-semibold">
                Actual Value
              </span>
              <span
                data-testid="value-individual-total"
                className="font-display text-3xl font-semibold tabular-nums"
              >
                {formatInr(actualValue)}
              </span>
            </div>
          </div>

          {/* Premium */}
          <div
            data-testid="value-premium-card"
            className="rounded-2xl border border-[#06B6D4]/50 bg-gradient-to-br from-[#06B6D4]/15 to-transparent p-6 sm:p-7 relative flex flex-col"
          >
            <div className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-[#06B6D4] text-[#0F172A] px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-bold">
              <Trophy className="h-3 w-3" /> Best Value
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#06B6D4] font-semibold pt-2">
              Premium Package — You Get Everything
            </div>
            <ul className="mt-5 space-y-2.5 text-sm flex-1">
              {includes.map((c) => (
                <li
                  key={c}
                  className="flex items-center gap-2.5 text-slate-100 font-medium"
                >
                  <span className="inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-400 text-[#0F172A]">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  {c}
                </li>
              ))}
            </ul>

            <div className="mt-6 pt-4 border-t border-white/10 space-y-2.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Actual Value</span>
                <span
                  data-testid="value-premium-strike"
                  className="text-slate-400 line-through tabular-nums"
                >
                  {formatInr(actualValue)}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <motion.span
                  data-testid="value-save-pill"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2.5 py-1 text-[11px] font-bold tracking-wide"
                >
                  <Flame className="h-3 w-3" /> SAVE {formatInr(savings)}
                </motion.span>
                <span
                  data-testid="value-discount-pill"
                  className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/40 px-2.5 py-1 text-[11px] font-bold tracking-wide"
                >
                  <Zap className="h-3 w-3" /> {discountPct}% OFF
                </span>
              </div>
              <div className="flex items-end justify-between pt-1">
                <span className="text-[10px] uppercase tracking-[0.22em] text-slate-300 font-semibold">
                  Today Only
                </span>
                <span
                  data-testid="value-premium-price"
                  className="font-display text-4xl font-semibold text-white tabular-nums"
                >
                  {formatInr(premium)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Hero savings highlight box */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          data-testid="value-hero-savings"
          className="mt-10 rounded-3xl overflow-hidden border border-emerald-400/30 bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent p-6 sm:p-8"
        >
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-300 font-semibold">
                Actual Value
              </div>
              <div className="mt-2 font-display text-2xl sm:text-3xl font-semibold text-slate-300 line-through tabular-nums">
                {formatInr(actualValue)}
              </div>
            </div>
            <div className="sm:border-x border-white/10">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#06B6D4] font-semibold">
                Today Only
              </div>
              <div className="mt-2 font-display text-3xl sm:text-4xl font-bold text-white tabular-nums">
                {formatInr(premium)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">
                You Save
              </div>
              <motion.div
                animate={{ scale: [1, 1.03, 1] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                className="mt-2 font-display text-3xl sm:text-4xl font-bold text-emerald-400 tabular-nums"
              >
                {formatInr(savings)}
              </motion.div>
            </div>
          </div>
          <div className="mt-6 flex justify-center">
            <motion.button
              type="button"
              onClick={handleUnlock}
              whileTap={{ scale: 0.97 }}
              animate={{ scale: [1, 1.04, 1] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              data-testid="value-savings-pill-hero"
              aria-label="Unlock Premium Package — proceed to checkout"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 text-[#0F172A] px-7 py-3.5 font-display text-base sm:text-lg font-bold tracking-tight shadow-[0_15px_40px_-10px_rgba(16,185,129,0.55)] hover:brightness-110 hover:shadow-[0_20px_50px_-10px_rgba(16,185,129,0.75)] cursor-pointer transition-all duration-300"
            >
              <Flame className="h-4 w-4" />
              Save {formatInr(savings)} Instantly — {discountPct}% OFF
              <ArrowRight className="h-4 w-4" />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

function UrgencyStrip() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45 }}
      data-testid="urgency-strip"
      className="mt-10 rounded-2xl border border-amber-400/30 bg-amber-400/10 px-5 py-4 sm:px-6 sm:py-5"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-center gap-3 sm:gap-6 text-amber-800 dark:text-amber-200 text-sm">
        <span className="inline-flex items-center gap-1.5 font-semibold">
          <Zap className="h-4 w-4" /> Founding Student Pricing
        </span>
        <span className="hidden sm:inline opacity-30">·</span>
        <span className="inline-flex items-center gap-1.5">
          <Flame className="h-4 w-4 text-rose-500" /> Early Access Offer
        </span>
        <span className="hidden sm:inline opacity-30">·</span>
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-4 w-4" /> Price may increase in future updates
        </span>
      </div>
    </motion.div>
  );
}

export default function Packages() {
  usePricing();
  const { addAllCourses, clear } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [glow, setGlow] = useState(false);

  const triggerHighlight = () => {
    setGlow(true);
    const el = document.getElementById("premium-package");
    if (el) {
      setTimeout(
        () => el.scrollIntoView({ behavior: "smooth", block: "center" }),
        100,
      );
    }
    setTimeout(() => setGlow(false), 3000);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("highlight") === "premium") {
      triggerHighlight();
    }
  }, [location.search]);

  useEffect(() => {
    const handler = () => triggerHighlight();
    window.addEventListener("highlight-premium-package", handler);
    return () => window.removeEventListener("highlight-premium-package", handler);
  }, []);

  const onSelect = (id) => {
    if (id === "premium") {
      addAllCourses();
      toast.success(`All ${PRICING.totalCourses} courses added — Premium auto-applied!`);
      navigate("/cart");
    } else {
      clear();
      const targetCount = (PRICING.tiers[id].pickCount || PRICING.tiers[id].courseCount);
      toast.success(
        `Pick ${targetCount} course${targetCount > 1 ? "s" : ""} below to apply ${
          id === "elite" ? "E-Lite" : "Basic"
        }.`,
      );
      const el = document.getElementById("courses");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section id="packages" data-testid="section-packages" className="py-14 sm:py-20">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-[11px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
            Choose Your Learning Path
          </span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-[#0F172A] dark:text-white text-balance leading-[1.05]">
            Pick the package that matches your goals.
          </h2>
          <p className="mt-5 text-slate-600 dark:text-slate-300 leading-relaxed">
            Unlock practical learning, mentorship and a community that helps
            you actually ship. Three simple access levels — no hidden fees.
          </p>
        </div>

        <div className="mt-10 grid lg:grid-cols-3 gap-6 lg:items-start">
          {buildPackages().map((p, i) => (
            <PackageCard
              key={p.id}
              pkg={p}
              idx={i}
              onSelect={onSelect}
              glow={glow && p.id === "premium"}
            />
          ))}
        </div>

        <p className="mt-8 text-center text-sm text-slate-500 dark:text-slate-400">
          One-time payment · Lifetime access to your courses · Secure checkout via Razorpay
        </p>

        <ValueComparison />
        <UrgencyStrip />
      </div>
    </section>
  );
}
